<template>
  <div class="app-container asset-claim-page">
    <div class="page-title">
      <div>
        <h3>领用申请台账</h3>
        <p>查看管理范围内全部领用申请，待处理申请可直接分配或驳回</p>
      </div>
      <el-button icon="el-icon-refresh" @click="loadList">刷新</el-button>
    </div>

    <el-form :model="filters" inline class="claim-query">
      <el-form-item label="申请搜索">
        <el-input v-model="filters.keyword" clearable placeholder="申请单号/申请人/资产" />
      </el-form-item>
      <el-form-item v-if="showAssetManagementSelector" label="管理归属">
        <el-select v-model="filters.managementType" clearable placeholder="全部">
          <el-option label="人事行政" value="HR" />
          <el-option label="仓库" value="WAREHOUSE" />
        </el-select>
      </el-form-item>
      <el-form-item label="申请状态">
        <el-select v-model="filters.status" clearable placeholder="全部状态">
          <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
    </el-form>

    <el-table v-loading="loading" :data="pagedClaims" border>
      <el-table-column label="申请单号" min-width="190">
        <template slot-scope="scope"><el-button type="text" @click="openDetail(scope.row)">{{ scope.row.requestNo || '-' }}</el-button></template>
      </el-table-column>
      <el-table-column label="申请人" prop="applicantUserNameSnapshot" width="110" />
      <el-table-column label="部门" prop="applicantDeptNameSnapshot" width="130" show-overflow-tooltip />
      <el-table-column label="管理归属" width="110" align="center">
        <template slot-scope="scope"><el-tag size="mini" :type="scope.row.managementType === 'HR' ? 'primary' : 'warning'">{{ managementText(scope.row.managementType) }}</el-tag></template>
      </el-table-column>
      <el-table-column label="资产分类" prop="categoryNameSnapshot" width="120" show-overflow-tooltip />
      <el-table-column label="申请资产" prop="requestedAssetName" min-width="160" show-overflow-tooltip />
      <el-table-column label="用途" prop="purpose" min-width="190" show-overflow-tooltip />
      <el-table-column label="状态" width="120" align="center">
        <template slot-scope="scope"><el-tag size="mini" :type="statusTag(scope.row.status)">{{ statusText(scope.row.status) }}</el-tag></template>
      </el-table-column>
      <el-table-column label="已分配资产" min-width="180" show-overflow-tooltip>
        <template slot-scope="scope">{{ assignedAssetText(scope.row) }}</template>
      </el-table-column>
      <el-table-column label="申请时间" prop="createTime" width="165" />
      <el-table-column label="操作" width="120" fixed="right" align="center">
        <template slot-scope="scope">
          <el-button v-if="canHandle(scope.row)" type="text" @click="openHandle(scope.row)">处理申请</el-button>
          <el-button v-else type="text" @click="openDetail(scope.row)">查看详情</el-button>
        </template>
      </el-table-column>
    </el-table>
    <pagination v-show="filteredClaims.length > 0" :total="filteredClaims.length" :page.sync="pageNum" :limit.sync="pageSize" :page-sizes="[10]" />
    <el-empty v-if="!loading && !filteredClaims.length" description="暂无领用申请记录" />

    <asset-claim-process-dialog :visible.sync="handleOpen" :record="current" @success="loadList" />

    <asset-order-detail-dialog :visible.sync="detailOpen" type="assetClaim" :record="current" />
  </div>
</template>

<script>
import assetManagementScope from '@/mixins/assetManagementScope'
import AssetClaimProcessDialog from './claimProcessDialog'
import AssetOrderDetailDialog from './orderDetailDialog'
import { listAssetClaims } from '@/api/crm/asset'
import { mapState } from 'vuex'

export default {
  name: 'CrmAssetClaimManagement',
  components: { AssetClaimProcessDialog, AssetOrderDetailDialog },
  mixins: [assetManagementScope],
  data() {
    return {
      loading: false,
      claims: [],
      filters: { keyword: '', managementType: '', status: '' },
      statusOptions: [
        { value: 10, label: '待资产管理员处理' }, { value: 20, label: '待领用人确认' },
        { value: 30, label: '已完成' }, { value: 40, label: '已驳回' }, { value: 50, label: '已取消' }
      ],
      pageNum: 1,
      pageSize: 10,
      handleOpen: false,
      current: {},
      detailOpen: false
    }
  },
  computed: {
    ...mapState({ currentUserId: state => state.user.userId }),
    filteredClaims() {
      const keyword = String(this.filters.keyword || '').trim().toLowerCase()
      return this.claims.filter(item => {
        if (this.filters.managementType && item.managementType !== this.filters.managementType) return false
        if (this.filters.status !== '' && Number(item.status) !== Number(this.filters.status)) return false
        if (!keyword) return true
        return [item.requestNo, item.applicantUserNameSnapshot, item.applicantDeptNameSnapshot, item.requestedAssetName, item.categoryNameSnapshot]
          .some(value => String(value || '').toLowerCase().includes(keyword))
      })
    },
    pagedClaims() {
      const start = (this.pageNum - 1) * this.pageSize
      return this.filteredClaims.slice(start, start + this.pageSize)
    }
  },
  watch: {
    'filters.keyword'() { this.pageNum = 1 },
    'filters.managementType'() { this.pageNum = 1 },
    'filters.status'() { this.pageNum = 1 }
  },
  created() { this.applyAssetManagementDefault(this.filters); this.loadList() },
  methods: {
    loadList() {
      this.loading = true
      listAssetClaims().then(res => { this.claims = res.data || []; this.pageNum = 1 }).finally(() => { this.loading = false })
    },
    managementText(value) { return value === 'HR' ? '人事行政' : '仓库' },
    statusText(value) { const item = this.statusOptions.find(option => Number(option.value) === Number(value)); return item ? item.label : '-' },
    statusTag(value) { return ({ 10: 'warning', 20: 'primary', 30: 'success', 40: 'danger', 50: 'info' })[Number(value)] || 'info' },
    canHandle(row) {
      return Number(row.status) === 10 && this.currentUserId != null &&
        String(row.applicantUserId) !== String(this.currentUserId)
    },
    assignedAssetText(row) { return row.assignedAssetId ? `${row.assignedAssetCodeSnapshot || '-'} / ${row.assignedAssetNameSnapshot || '-'}` : '-' },
    openHandle(row) {
      this.current = row
      this.handleOpen = true
    },
    openDetail(row) { this.current = row; this.detailOpen = true }
  }
}
</script>

<style scoped>
.page-title { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.page-title h3 { margin:0 0 6px; }
.page-title p { margin:0; color:#909399; }
.claim-query { padding:14px 14px 0; margin-bottom:14px; border-radius:6px; background:#f8fafc; }
</style>
